import React, { useCallback, useMemo, useState } from "react";

import { AHText, Box, Input } from "arkh";

import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { UserLoggedInNavigatorNavigationProp } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

import { useIntlTokens } from "../../../components/mobile/useIntlTokens";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../../common/navigationHeaderOptions";
import { OrgAdminTopicsCreateMutation } from "./__generated__/OrgAdminTopicsCreateMutation.graphql";
import { useIntl } from "react-intl";
import { StoreSelector } from "../WorkspaceEntity/StoreSelector";
import { OrgAdminTopicsCreateSelectStoresQuery } from "./__generated__/OrgAdminTopicsCreateSelectStoresQuery.graphql";
import {
  ReceiverStoreAllTeamsIndicator,
  ReceiverStoreTeamsIndicator,
  ReceiverStoreMain,
  ReceiverStoreNoTeamsIndicator,
} from "../WorkspaceEntity/ReceiverStoresSelector";
import { OrgAdminTeamSelector } from "../OrgAdmin/OrgAdminTeams/OrgAdminTeamsSelector";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { OrgAdminTopicsPrivateToggle } from "./OrgAdminTopicsPrivateToggle";

type ParamList = {
  OrgAdminTopicsCreateScreen: object;
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgAdminTopicsCreate = withWebModal(() => {
  const getOptions = useNavigationHeaderOptions();
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="OrgAdminTopicsCreateScreen"
        component={OrgAdminTopicsCreateScreen}
        options={({ navigation }) =>
          getOptions({
            type: "CREATE",
            onCancel: () => {
              navigation.goBack();
            },
            title: "",
            onSave: () => {},
            saveLoading: false,
            disabled: true,
          })
        }
      />
    </Stack.Navigator>
  );
});

type OrgUserInviteScreenProps = {
  route: RouteProp<ParamList, "OrgAdminTopicsCreateScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgAdminTopicsCreate">,
    NativeStackNavigationProp<ParamList, "OrgAdminTopicsCreateScreen">
  >;
};

function OrgAdminTopicsCreateScreen(props: OrgUserInviteScreenProps) {
  const intl_tokens = useIntlTokens();
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const disabled = name.trim() === "";
  const [createTopic, createTopicLoading] =
    useMutation<OrgAdminTopicsCreateMutation>(graphql`
      mutation OrgAdminTopicsCreateMutation(
        $name: String!
        $description: String!
        $storeIds: [ID!]!
        $teamIds: [ID!]
        $isPrivate: Boolean!
      ) {
        organisation {
          createTopic(
            input: {
              name: $name
              description: $description
              storeIds: $storeIds
              teamIds: $teamIds
              isPrivate: $isPrivate
            }
          ) {
            id
            topics {
              id
            }
          }
        }
      }
    `);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [selectedRoutedTeamIds, setSelectedRoutedTeamIds] = useState<string[]>(
    []
  );
  const [isPrivate, _setIsPrivate] = useState(false);
  function setIsPrivate(value: boolean) {
    _setIsPrivate(value);
    setSelectedRoutedTeamIds([]);
  }

  const data = useLazyLoadQuery<OrgAdminTopicsCreateSelectStoresQuery>(
    graphql`
      query OrgAdminTopicsCreateSelectStoresQuery {
        organisation {
          stores {
            id
            allTeams {
              id
              isPrivate
              ...ReceiverStoresSelectorTeamsIndicator_teams
              ...OrgAdminTeamsSelector_teams
            }

            ...StoreSelector_store
            ...ReceiverStoresSelectorMain_receiverStore
          }
        }
      }
    `,
    {},
    {
      fetchPolicy: "store-and-network",
    }
  );

  const [selectedType, setSelectedType] = useState<"STORES" | "TEAMS">(
    "STORES"
  );

  const stores = data.organisation?.stores ?? [];
  const allStoreTeams = useMemo(
    () =>
      data.organisation?.stores.flatMap((store) =>
        selectedStoreIds.includes(store.id)
          ? (
              store.allTeams?.filter((t) => {
                if (!t) {
                  return false;
                }
                return isPrivate === t.isPrivate;
              }) ?? []
            ).map((t) => [t, store.id] as const)
          : []
      ) ?? [],
    [data.organisation?.stores, isPrivate, selectedStoreIds]
  );
  const teamIdsToSave = useMemo(() => {
    return allStoreTeams
      .map(([team]) => team.id)
      .filter((s) => selectedRoutedTeamIds.includes(s));
  }, [allStoreTeams, selectedRoutedTeamIds]);

  const allTeams = useMemo(() => {
    return allStoreTeams.map(([t]) => t);
  }, [allStoreTeams]);

  const receiverStoreById = useMemo(() => {
    const map = new Map();
    data.organisation?.stores.forEach((store) => {
      map.set(store.id, store);
    });
    return map;
  }, [data.organisation?.stores]);

  const teamsPerStore = useMemo(() => {
    const map = new Map();
    allStoreTeams.forEach(([team, storeId]) => {
      if (selectedRoutedTeamIds.includes(team.id)) {
        const teams = map.get(storeId) || [];
        teams.push(team);
        map.set(storeId, teams);
      }
    });
    return map;
  }, [allStoreTeams, selectedRoutedTeamIds]);

  const onCreateTopic = useCallback(() => {
    createTopic({
      variables: {
        name,
        description,
        storeIds: selectedStoreIds,
        teamIds: teamIdsToSave,
        isPrivate,
      },
      onCompleted: (data) => {
        props.navigation.goBack();
      },
    });
  }, [
    createTopic,
    name,
    description,
    selectedStoreIds,
    teamIdsToSave,
    isPrivate,
    props.navigation,
  ]);
  const intl = useIntl();
  useNavigationHeader({
    type: "CREATE",
    title: intl.formatMessage({
      defaultMessage: "Create Attribute",
      description: "OrgAdminTopicsCreate title",
    }),
    createButtonLabel: intl_tokens.Create,
    defaultContentStyle: true,
    onCancel: () => {
      props.navigation.goBack();
    },
    disabled,
    onSave: onCreateTopic,

    saveLoading: createTopicLoading,
  });
  return (
    <Box space="medium" gap="medium" style={{ flex: 1 }}>
      <Input
        value={name}
        onChangeText={setName}
        placeholder={intl_tokens.Name}
        textContentType="name"
      />
      <Input
        value={description}
        onChangeText={setDescription}
        placeholder={intl_tokens.Description}
        multiline={true}
      />
      <OrgAdminTopicsPrivateToggle
        isPrivate={isPrivate}
        onChange={setIsPrivate}
      />
      <SegmentedControlNew
        items={[
          {
            children: <AHText type="subtitle">{intl_tokens.Units}</AHText>,
            width: 100,
            value: "STORES",
          },
          {
            width: 100,
            children: <AHText type="subtitle">{intl_tokens.Teams}</AHText>,
            value: "TEAMS",
          },
        ]}
        selected={selectedType}
        setSelected={(selected) => {
          setSelectedType(selected);
        }}
      />

      {selectedType === "STORES" ? (
        <Box
          style={{ flex: 1, overflow: "hidden", borderWidth: 1 }}
          cornerRadius="large"
          borderColor="border-primary"
        >
          <StoreSelector
            stores={stores}
            selectedStoreIds={selectedStoreIds}
            onStoreSetSelect={setSelectedStoreIds}
            renderItem={(store, selected, index) => {
              const receiverStore = receiverStoreById.get(store.id);

              if (!receiverStore) {
                return null;
              }

              const teams = selected ? teamsPerStore.get(store.id) || [] : [];

              return (
                <ReceiverStoreMain
                  store={receiverStore}
                  right={
                    selected ? (
                      <>
                        {teams.length === 0 ? (
                          isPrivate ? (
                            <ReceiverStoreNoTeamsIndicator />
                          ) : (
                            <ReceiverStoreAllTeamsIndicator />
                          )
                        ) : (
                          <ReceiverStoreTeamsIndicator teams={teams} />
                        )}
                      </>
                    ) : null
                  }
                  selected={selected}
                  isLast={index === stores.length - 1}
                  onPress={() => {
                    setSelectedStoreIds(
                      selected
                        ? selectedStoreIds.filter((id) => id !== store.id)
                        : [...selectedStoreIds, store.id]
                    );
                  }}
                  disabled={false}
                />
              );
            }}
          />
        </Box>
      ) : (
        <Box style={{ flex: 1, overflow: "hidden" }}>
          <OrgAdminTeamSelector
            teams={allTeams}
            selectedTeams={selectedRoutedTeamIds}
            setSelectedTeams={setSelectedRoutedTeamIds}
          />
        </Box>
      )}
    </Box>
  );
}
